import { ILiftSettings } from '@llws/lift-entity-interfaces';
import { Route } from 'vue-router';
import { CRUD, CRUDProducts, LongToken } from '@llws/platform-shared-types/types/auth';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { ICurrentUser } from '@/shared/interfaces/current-user';
import app from '@/main';
import { TAccessType } from '../types/t-access-type';
import { ICurrentClient } from '../interfaces/current-client';
import { forceToArray } from '../helpers/force-to-array';
import { ERouteMetaPermissionMode, IRouteMetaPermissionConfig } from '../interfaces/route-config-meta';
import { ISelectOption } from '../interfaces';
import { IClientPremiumProducts } from '../interfaces/premium-products';
import { TPremiumProductKey } from '../types/premium-products';

export class CurrentUser implements ICurrentUser {
  public userId!: string;
  public currentClientId!: string;
  public email!: string;
  public firstName!: string;
  public lastName!: string;
  public isSuperUser!: boolean;
  public isAdmin!: boolean;
  public exp!: number;
  public iat!: number;
  public refId!: number;
  public client!: ICurrentClient;
  public readonly products: LongToken['products'];
  public readonly premiumProducts: IClientPremiumProducts;

  constructor(user: ICurrentUser) {
    Object.keys(user).forEach((k) => {
      (this as Record<string, any>)[k] = (user as Record<string, any>)[k];
    });

    const accountProducts = this.products?.account;
    this.isSuperUser = !!accountProducts && !!accountProducts['-1']?.superuser;
    this.isAdmin = !!accountProducts && !!accountProducts['-1']?.client_settings?.d;
    this.currentClientId = `${this.client.id}`;
  }

  public hasRouterPermission(
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
    route?: Route,
  ) {
    if (typeof forceToArray<string | IRouteMetaPermissionConfig>(permissionKeys)[0] !== 'string') {
      return this.hasAccessBasedOnConfig(forceToArray<IRouteMetaPermissionConfig>(permissionKeys as IRouteMetaPermissionConfig), route);
    }
    return this.hasAccessBasedOnStringArray(forceToArray<string>(permissionKeys as string | string[]));
  }

  public hasPermission(
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
  ): boolean {
    if (typeof forceToArray<string | IRouteMetaPermissionConfig>(permissionKeys)[0] !== 'string') {
      return this.hasAccessBasedOnConfig(forceToArray<IRouteMetaPermissionConfig>(permissionKeys as IRouteMetaPermissionConfig));
    }

    return this.hasAccessBasedOnStringArray(forceToArray<string>(permissionKeys as string | string[]));
  }

  public hasPermissionAccess(
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
    accessType?: TAccessType,
    id?: string | number,
  ): boolean {
    if (typeof forceToArray<string | IRouteMetaPermissionConfig>(permissionKeys)[0] !== 'string') {
      return this.hasAccessBasedOnConfig(forceToArray<IRouteMetaPermissionConfig>(permissionKeys as IRouteMetaPermissionConfig));
    }
    return this.hasAccessBasedOnStringArray(forceToArray<string>(permissionKeys as string | string[]), accessType, id);
  }

  public hasAtLeastOneModel(
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
    accessType?: TAccessType,
  ): boolean {
    return this.hasAtLeastOneModelArray(ModelType.building, forceToArray<string>(permissionKeys as string | string[]), accessType ?? 'read')
      || this.hasAtLeastOneModelArray(ModelType.account, forceToArray<string>(permissionKeys as string | string[]), accessType ?? 'read')
      || this.hasAtLeastOneModelArray(ModelType.site, forceToArray<string>(permissionKeys as string | string[]), accessType ?? 'read');
  }

  public hasAtLeastOneProduct(
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
    accessType?: TAccessType,
  ): boolean {
    return this.hasAtLeastOneProductArray(ModelType.building, permissionKeys, accessType ?? 'read')
      || this.hasAtLeastOneProductArray(ModelType.account, permissionKeys, accessType ?? 'read')
      || this.hasAtLeastOneProductArray(ModelType.site, permissionKeys, accessType ?? 'read');
  }

  public getPermittedSiteOptions(): ISelectOption[] {
    if ((this.products?.site ?? {})[-1]) {
      return ([...(this.client.ownedSites ?? [])])
        .map((site) => ({ text: site.name, value: site.id! }));
    }

    return (
      this.products?.site
        ? ([...(this.client.ownedSites ?? []), ...(this.client.sites ?? [])])
          .reduce((acc, cur) => [
            ...acc,
            ...(
              (this.products?.site ?? {})[cur.id!]
              && Object.keys((this.products?.site ?? {})).length
              && !acc.some((s) => s.value === cur.id!)
                ? [{ text: cur.name, value: cur.id! }]
                : []
            ),
          ], [] as ISelectOption[])
        : []
    );
  }

  private hasAtLeastOneModelArray(modelType: ModelType, permissionKeys: string[], accessType?: TAccessType) {
    const models: CRUDProducts[] = Object.values((this.products ?? {})[modelType] ?? {});

    return models.some(
      (model) => permissionKeys.every((p) => {
        if (modelType === ModelType.site && p === 'sites') {
          const modelProducts = Object.values(model);
          return modelProducts.some((modelProduct) => modelProduct[(accessType?.charAt(0) as keyof CRUD) || 'r'] === true);
        }
        return model[p] && model[p][(accessType?.charAt(0) as keyof CRUD) || 'r'] === true;
      }),
    );
  }

  private hasAtleastOneProductBasedOnConfigArray(permissions: IRouteMetaPermissionConfig[], modelType: ModelType, accessType?: TAccessType) {
    const models: CRUDProducts[] = Object.values((this.products ?? {})[modelType] ?? {});
    const getCallback = (permission: IRouteMetaPermissionConfig) => (model: CRUDProducts) => {
      if (modelType === ModelType.site && permission.key === 'sites') {
        const modelProducts = Object.values(model);
        return modelProducts.some((modelProduct) => modelProduct[((permission.accessType || accessType)?.charAt(0) as keyof CRUD) || 'r'] === true);
      }
      return model[permission.key] && model[permission.key][((permission.accessType || accessType)?.charAt(0) as keyof CRUD) || 'r'] === true;
    };
    return permissions.some((p) => {
      if (p.checkAtLeastOneModel) {
        return models.some(getCallback(p));
      }
      return models.every(getCallback(p));
    });
  }

  private hasAtleastOneProductBasedOnStringArray(permissions: string[], modelType: ModelType, accessType?: TAccessType) {
    const models: CRUDProducts[] = Object.values((this.products ?? {})[modelType] ?? {});
    return models.every((model) => permissions.some((p) => {
      if (modelType === ModelType.site && p === 'sites') {
        const modelProducts = Object.values(model);
        return modelProducts.some((modelProduct) => modelProduct[(accessType?.charAt(0) as keyof CRUD) || 'r'] === true);
      }
      return model[p] && model[p][(accessType?.charAt(0) as keyof CRUD) || 'r'] === true;
    }));
  }

  private hasAtLeastOneProductArray(
    modelType: ModelType,
    permissionKeys: string | string[] | IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
    accessType?: TAccessType,
  ) {
    if (typeof forceToArray<string | IRouteMetaPermissionConfig>(permissionKeys)[0] !== 'string') {
      return this.hasAtleastOneProductBasedOnConfigArray(
        forceToArray<IRouteMetaPermissionConfig>(
          permissionKeys as IRouteMetaPermissionConfig[] | IRouteMetaPermissionConfig,
        ),
        modelType,
        accessType,
      );
    }
    return this.hasAtleastOneProductBasedOnStringArray(forceToArray<string>(permissionKeys as string[] | string), modelType, accessType);
  }

  private getAllProducts(productType: ModelType) {
    return (((this.products ?? {})[productType] ?? {}))[-1] ?? {};
  }

  /**
   * Retrieves an array of property IDs that have the specified premium product enabled.
   * Optionally filters the products by what the user has access to.
   */
  public getPremiumProductPropertyIds(premiumProduct: TPremiumProductKey, filterByUserPerms: boolean = false) {
    const building = this.premiumProducts?.building ?? {};
    if (Object.keys(building).length === 0) {
      return [];
    }

    const propertyIds: string[] = [];
    Object.entries(building).forEach(([propertyId, productKeys]) => {
      if (productKeys.includes(premiumProduct)) {
        propertyIds.push(propertyId);
      }
    });

    if (!filterByUserPerms) {
      return propertyIds;
    }

    return propertyIds.filter((p) => this.hasPermissionAccess([ premiumProduct ], 'read', p));
  }

  private hasAccessToProduct(productKey: string, accessType?: TAccessType, modelId: number | string = -1) {
    return this.products?.building?.[Number(modelId)]?.[productKey]?.[(accessType?.charAt(0) as keyof CRUD) || 'r']
        || this.products?.site?.[Number(modelId)]?.[productKey]?.[(accessType?.charAt(0) as keyof CRUD) || 'r']
        || this.products?.account?.[Number(modelId)]?.[productKey]?.[(accessType?.charAt(0) as keyof CRUD) || 'r'];
  }

  private hasAccessBasedOnStringArray(permissionKeys: string[], accessType?: TAccessType, modelId?: number | string) {
    if (permissionKeys.includes('superuser') && this.isSuperUser) {
      return true;
    }

    // TODO: remove once we add the credit check product to replace this
    if (permissionKeys.includes('admin') && [this.isAdmin, this.isSuperUser].includes(true)) {
      return true;
    }

    const allProductsPerms = {
      ...this.getAllProducts(ModelType.building),
      ...this.getAllProducts(ModelType.site),
      ...this.getAllProducts(ModelType.account),
    };
    const notAllowed = permissionKeys
      .filter((p) => !(allProductsPerms?.[p] ?? [])[(accessType?.charAt(0) as keyof CRUD) || 'r'])
      .filter((p) => !this.hasAccessToProduct(p, accessType, modelId));
    return notAllowed.length === 0;
  }

  private hasAccessBasedOnConfig(configs: IRouteMetaPermissionConfig[], route?: Route) {
    if (configs.length === 0) {
      return true;
    }

    const matches = configs.filter((c) => {
      if (['building', 'site'].includes(c.keyType) && c.mode === ERouteMetaPermissionMode.AT_LEAST_ONE) {
        return this.hasAtLeastOneModel(c.key, c.accessType ?? 'read');
      }

      const id = c.idVal || (c.idParam ? route?.params[c.idParam] || app()?.$route.params[c.idParam] || -1 : -1);

      if (c.keyType && Object.keys(ModelType).includes(c.keyType)) {
        return c.checkAtLeastOneModel
          ? this.hasAtLeastOneModelArray(ModelType[c.keyType], [c.key], c.accessType)
          : this.hasAccessBasedOnStringArray([c.key], c.accessType, id);
      }

      return this.hasAccessBasedOnStringArray([`${(c.key === 'sites' ? 'pages' : c.key)}`], c.accessType, id);
    });

    return matches.length === configs.length;
  }

  public hasAdminOrSuperUserAccess(): boolean {
    return this.isAdmin || this.isSuperUser;
  }

  public getClientSettingValue(key: string): string | -1 {
    if (key === 'suite_types_legacy') {
      return '1';
    }

    return this.client.liftSettings!.find((v) => v.key === key)?.value || -1;
  }

  public getClientSetting(key: string): ILiftSettings | -1 {
    return this.client.liftSettings!.find((v) => v.key === key) || -1;
  }

  public clientTimezone(): string {
    return this.client.timeZone || 'Canada/Eastern';
  }

  public fullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  public getModelIdsByProductsAndModel(products: string[], model: ModelType) {
    if (this.products && this.products[model]) {
      return Object
        .keys(this.products[model])
        .filter((key) => {
          const modelProducts = Object.keys(this.products![model]![Number(key)]);
          return modelProducts.some((mp) => products.includes(mp));
        });
    }

    return [];
  }
}
